import Immutable from 'immutable';
import React, { Dispatch } from 'react';
import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { DragSourceType, EntityType, Layer, MinCellWidth } from 'const';
import { isDraggingAnchor as isDraggingAnchorSelector } from 'containers/App/selectors';
import { addTextToCell, dropAsset } from 'containers/ArtboardCell/actions';
import * as Models from 'models';
import { prioritizeActiveLayerInLayeredRelation } from 'utils/prioritizeActiveLayerInLayeredRelations';

const ALLOWED_DROP_ENTITY_TYPES: readonly EntityType[] = [
  // ANCHOR is allowed but handled separately
  // EntityType.ANCHOR,
  EntityType.CALL_TO_ACTION,
  EntityType.IMAGE,
  EntityType.REFERENCE_CITATION,
  EntityType.TEXT,
  EntityType.LAYOUT_RENDITION,
];

type CanDropProps = {
  notEditable: boolean;
  isReadOnly: boolean;
  width: number;
  isCarouselControls: boolean;
  isReusableLayout: boolean;
};

function canDropFn(props: CanDropProps, item: Models.AssetDragObject, isDraggingAnchor: boolean): boolean {
  const { notEditable, isReadOnly, width, isCarouselControls, isReusableLayout } = props;
  if (isCarouselControls) {
    return false;
  }
  const { type } = item;

  if (isReadOnly && isReusableLayout) {
    return false;
  }

  if (notEditable) {
    return isDraggingAnchor || (isReadOnly && ALLOWED_DROP_ENTITY_TYPES.includes(type));
  }
  if (type === EntityType.CALL_TO_ACTION) {
    return width >= MinCellWidth.CTA;
  } else {
    return ALLOWED_DROP_ENTITY_TYPES.includes(type);
  }
}

type DropProps = {
  activeLayer: Layer;
  relation: Models.LayeredRegularRelationMap<Models.CombinedAssetRelationStyles>;
  sectionStyles: Models.MasterScreen.SectionStylesMap;
  layoutId: string;
};

function dropFn(props: DropProps, item: Models.AssetDragComponent, dispatch: Dispatch<AnyAction>): void {
  const { component, type } = item;
  const {
    activeLayer,
    relation: layeredRelation,
    sectionStyles,
    layoutId,
  } = props;
  const relation = prioritizeActiveLayerInLayeredRelation(layeredRelation, activeLayer);

  if ([EntityType.CALL_TO_ACTION, EntityType.IMAGE, EntityType.LAYOUT_RENDITION].includes(type)) {
    dispatch(dropAsset(
      relation as
      Models.RegularRelationMap<Models.CallToActionStyles> |
      Models.RegularRelationMap<Models.ImageRelationStyles> |
      Models.RegularRelationMap<Models.LayoutStyles>,
      component as Models.CallToActionMap | Models.ImageMap | Models.ReusableLayoutMap,
      layoutId,
      (component as Models.CallToActionMap).get('size'),
    ));
  } else if (type === EntityType.TEXT) {
    dispatch(addTextToCell(
      relation.get('id'),
      Immutable.Map({ [relation.get('id')]: relation }) as Models.RelationsMap,
      component as Models.TextComponentMap,
      layoutId,
      sectionStyles,
      true,
    ));
  }
}

type Props = {
  container: React.RefObject<HTMLDivElement>;
  notEditable: boolean;
  isReadOnly: boolean;
  width: number;
  activeLayer: Layer;
  relation: Models.LayeredRegularRelationMap<Models.CombinedAssetRelationStyles>;
  sectionStyles: Models.MasterScreen.SectionStylesMap;
  layoutId: string;
  isCarouselControls: boolean;
  isReusableLayout: boolean;
};

type DropTargetHook = {
  isOver: boolean;
  canDropHere: boolean;
  dropItem: Models.AssetDragComponent | null;
  isDraggingAnchor: boolean;
};

export const ARTBOARD_CELL_ACCEPTED_DROP_TYPE = DragSourceType.ASSET;

export const useArtboardCellDropTarget = (props: Props): DropTargetHook => {
  const dispatch = useDispatch();
  const isDraggingAnchor = useSelector(isDraggingAnchorSelector);
  const { container } = props;
  const [{ isOver, canDrop, dropItem }, drop] = useDrop({
    accept: [ARTBOARD_CELL_ACCEPTED_DROP_TYPE],
    canDrop: (item: Models.AssetDragObject) => canDropFn(props, item, isDraggingAnchor),
    drop: (item: Models.AssetDragComponent) => dropFn(props, item, dispatch),
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
      dropItem: monitor.getItem(),
    }),
  });
  const canDropHere = isOver && canDrop;
  drop(container);

  return {
    isDraggingAnchor,
    isOver,
    canDropHere,
    dropItem,
  };
};
